import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '../api/auth.api';
import { User } from '../gen/proto/api/gateway/v1/gateway_pb';
import { userApi } from '../api/user.api';

interface AuthStore {
  user: User | null;
  authToken: string;
  refreshToken: string;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (username: string, password: string, email: string) => Promise<void>;
  checkToken: () => Promise<boolean>;
  setToken: (authToken: string, refreshToken: string) => void;

  getUserUuid: () => string;
  getUsername: () => string;
}

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
  authToken: '',
  refreshToken: '',
  user: null,
  isAuthenticated: false,

  login: async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    set({
      authToken: response.accessToken,
      refreshToken: response.refreshToken,
      isAuthenticated: true,
    });

    const userResponse = await userApi.getByEmail(email);
    set({
      user: userResponse,
    });
  },

  logout: async () => {
    const { refreshToken } = get();
    if (refreshToken) {
      await authApi.logout(refreshToken);
    }
    set({
      authToken: '',
      refreshToken: '',
      user: null,
      isAuthenticated: false,
    });
  },

  register: async (username: string, password: string, email: string) => {
    const response = await authApi.register({
      username,
      password,
      email,
    });
    set({
      authToken: response.accessToken,
      refreshToken: response.refreshToken,
      isAuthenticated: true,
    });

    const userResponse = await userApi.getByEmail(email);
    set({
      user: userResponse,
    });
  },

  checkToken: async () => {
    const { isAuthenticated, authToken } = get();
    if (!isAuthenticated) return false;

    const response = await authApi.checkToken(authToken);
    if (response.tokenValid) {
      set({
        isAuthenticated: true,
      });
      return true;
    }

    set({
      authToken: '',
      refreshToken: '',
      user: null,
      isAuthenticated: false,
    });
    return false;
  },

  getUserUuid() {
    return get().user?.uuid || '';
  },
  getUsername() {
    return get().user?.username || '';
  },

  setToken(authToken: string, refreshToken: string) {
    set({
      authToken,
      refreshToken,
    });
  },
}),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        authToken: state.authToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
);

export default useAuthStore;
